import { chain } from "lodash";
import Papa from "papaparse";
import {
  Document,
  DocumentAnswer,
  DocumentAnswerSuggestion,
  DocumentVersion,
} from "../../../stores/models";
import {
  AnswerItem,
  AnswerItemsElement,
  MultiStepFormInputElement,
  StepValue,
  TableElement,
  TemplateConfig,
  TemplateQuestion,
} from "../../../types";
import { captureSentryException, loadDocumentReferences } from "../../../utils";

export function getLatestSavedSuggestion(
  step: TemplateQuestion,
  documentVersion: DocumentVersion
): DocumentAnswerSuggestion | undefined {
  return chain(documentVersion.suggestions)
    .filter((s) => s.element === step.id)
    .orderBy("createdAt")
    .last()
    .value();
}

export function getAnswerFromDocumentVersion(
  step: MultiStepFormInputElement,
  documentVersion: DocumentVersion
): DocumentAnswer | undefined {
  return documentVersion.answers.find(
    (s: DocumentAnswer) => s.element === step.id
  );
}

export const getAnswer = ({
  currentStepValue,
  step,
  documentVersion,
  documents,
  templatesConfig,
}: {
  currentStepValue?: StepValue | undefined;
  step: TemplateQuestion;
  documentVersion?: DocumentVersion | undefined;
  documents: Document[];
  templatesConfig: TemplateConfig;
}): StepValue => {
  let stepValue: StepValue = {
    answer: undefined,
    answerFileId: undefined,
    answerItems: undefined,
  };

  let savedAnswer: DocumentAnswer | undefined = undefined;

  if (documentVersion) {
    savedAnswer = getAnswerFromDocumentVersion(step.element, documentVersion);
  }

  const hasTransformer = step.element.transformerConfig !== undefined;

  if (
    step.element.type === "fileUpload" &&
    (currentStepValue?.answerFileId || savedAnswer?.answerFileId)
  ) {
    stepValue.answerFileId =
      currentStepValue?.answerFileId || savedAnswer?.answerFileId;
  } else if (
    step.element.type === "answerItemsElement" &&
    (currentStepValue?.answerItems?.length || savedAnswer?.answerItems?.length)
  ) {
    stepValue.answerItems =
      currentStepValue?.answerItems || savedAnswer?.answerItems;
  } else if (
    !["fileUpload", "answerItemsElement"].includes(step.element.type) &&
    (currentStepValue?.answer || savedAnswer?.answer)
  ) {
    stepValue.answer = currentStepValue?.answer || savedAnswer?.answer;
  } else if ((!savedAnswer || !currentStepValue) && hasTransformer) {
    stepValue.answer = runTransformer({
      step,
      documentVersion,
      documents,
      templatesConfig,
    });

    // Else use the default answer that the state has been initialized with
  } else if (step.element.options.default) {
    stepValue.answer = step.element.options.default;
  }
  return stepValue;
};

export const runTransformer = ({
  step,
  documentVersion,
  documents,
  templatesConfig,
}: {
  step: TemplateQuestion;
  documentVersion?: DocumentVersion;
  documents: Document[];
  templatesConfig: TemplateConfig;
}) => {
  // load data
  if (!step.element.transformerConfig) {
    throw new Error(
      `No transformerConfig defined for element ${step.element.id}`
    );
  }
  const inputs = step.element.transformerConfig.inputs.map((input) => {
    // check if answer is in current document
    let answerForInput = documentVersion?.answers.find(
      (a) => a.element === input
    )?.answer;

    // check if answer is in any document
    if (!answerForInput) {
      answerForInput = documents
        .find((doc) =>
          doc.versions[0]?.answers.find((a) => a.element === input)
        )
        ?.versions[0]?.answers.find((a) => a.element === input)?.answer;
    }

    if (!answerForInput) {
      // TODO handle this better. This is not ideal and ideally the step should only render if all previous steps are completed. We had to remove the error throw because it was causing issues since some inputs are relying on previous inputs and since the transformer is run on every render, it was causing issues.
      answerForInput = "";
      // throw new Error(`No answer found for input ${input}`);
    }
    return { id: input, value: answerForInput };
  });

  const inputsObject: Record<string, string> = chain(inputs)
    .keyBy("id")
    .mapValues("value")
    .value() as Record<string, string>;

  if (
    step.element.transformerConfig.inputs.some(
      (key: string) => inputsObject[key] === undefined
    )
  ) {
    const message = `Missing required data (utils): ${JSON.stringify(
      inputsObject
    )}`;
    console.error(message);
    captureSentryException(message);
    return "";
  }

  const docReferences = loadDocumentReferences(documents, templatesConfig);

  const output = step.element.transformerConfig?.transformer(
    inputsObject,
    docReferences
  );

  return output;
};

interface CSVRow {
  [key: string]: unknown;
}

const normalizeCsvHeader = (header: string) => header.trim().toLowerCase();

const getHighestSequentialTypeNumber = (items: AnswerItem[]): number =>
  items.reduce(
    (highest, item) => Math.max(highest, item.sequentialTypeNumber || 0),
    0
  );

export const handleCSVUpload = (
  file: File,
  templateKey: string,
  elementId: string,
  templatesConfig: TemplateConfig
): Promise<string> => {
  const element = templatesConfig[templateKey]?.questions
    .filter((e): e is TemplateQuestion => e.hasOwnProperty("element"))
    .find((e) => e.id === elementId && e.element.type === "table");

  if (!element) {
    throw new Error("Element is not a table element");
  }

  const templateElement = element?.element as TableElement;

  const expectedColumns = templateElement.options.columns;

  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      complete: (results) => {
        const headers = Object.keys(results.data[0] as CSVRow).map((header) =>
          header.trim()
        );

        if (headers.length !== expectedColumns.length) {
          reject(
            new Error(
              `Too many columns in the CSV. Expected ${expectedColumns.length}, but found ${headers.length}.`
            )
          );
          return;
        }

        // Check if all expected columns are present (ignoring order)
        const missingColumns = expectedColumns
          .filter((col) => !headers.includes(col.name))
          .map((col) => col.name);
        if (missingColumns.length > 0) {
          reject(new Error(`Missing columns: ${missingColumns.join(", ")}`));
          return;
        }

        // Generate markdown table
        let markdownTable =
          "| " + expectedColumns.map((col) => col.name).join(" | ") + " |\n";
        markdownTable +=
          "| " + expectedColumns.map(() => "---").join(" | ") + " |\n";

        results.data.forEach((row) => {
          const typedRow = row as CSVRow;

          markdownTable +=
            "| " +
            expectedColumns
              .map((col) => {
                let value = typedRow[col.name] || "";
                if (typeof value === "string") {
                  value = value.replace(/\r?\n/g, "<br/>");
                }
                return value;
              })
              .join(" | ") +
            " |\n";
        });
        resolve(markdownTable);
      },
      error: (error) => {
        reject(new Error(`Error parsing CSV: ${error.message}`));
      },
      header: true,
      skipEmptyLines: true,
    });
  });
};

const buildAnswerItemsHeaderMap = (
  element: AnswerItemsElement,
  headers: string[]
): Record<string, string> => {
  const headerToFieldKey: Record<string, string> = {};
  const normalizedFieldKeyMap: Record<string, string> = {};

  element.options.fields.forEach((field) => {
    const fieldKeyNormalized = normalizeCsvHeader(field.fieldKey);
    const labelNormalized = normalizeCsvHeader(field.label);

    if (
      normalizedFieldKeyMap[fieldKeyNormalized] &&
      normalizedFieldKeyMap[fieldKeyNormalized] !== field.fieldKey
    ) {
      throw new Error(
        `Ambiguous column mapping for "${field.fieldKey}". Please use unique field keys or labels.`
      );
    }
    normalizedFieldKeyMap[fieldKeyNormalized] = field.fieldKey;

    if (labelNormalized !== fieldKeyNormalized) {
      if (
        normalizedFieldKeyMap[labelNormalized] &&
        normalizedFieldKeyMap[labelNormalized] !== field.fieldKey
      ) {
        throw new Error(
          `Ambiguous column mapping for "${field.label}". Please use unique field keys or labels.`
        );
      }
      normalizedFieldKeyMap[labelNormalized] = field.fieldKey;
    }
  });

  headers.forEach((header) => {
    const normalized = normalizeCsvHeader(header);
    const fieldKey = normalizedFieldKeyMap[normalized];
    if (!fieldKey) {
      throw new Error(`Unknown column: ${header}`);
    }
    if (headerToFieldKey[header]) {
      throw new Error(`Duplicate column: ${header}`);
    }
    const existingFieldKey = Object.values(headerToFieldKey).find(
      (key) => key === fieldKey
    );
    if (existingFieldKey) {
      throw new Error(
        `Multiple columns map to the same field: ${fieldKey}. Please keep only one column.`
      );
    }
    headerToFieldKey[header] = fieldKey;
  });

  const mappedFieldKeys = new Set(Object.values(headerToFieldKey));
  const missingRequired = element.options.fields
    .filter((field) => field.required)
    .filter((field) => !mappedFieldKeys.has(field.fieldKey))
    .map((field) => field.fieldKey);

  if (missingRequired.length > 0) {
    throw new Error(`Missing required columns: ${missingRequired.join(", ")}`);
  }

  return headerToFieldKey;
};

export const handleAnswerItemsCSVUpload = (
  file: File,
  element: AnswerItemsElement,
  existingItems: AnswerItem[]
): Promise<AnswerItem[]> =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers =
          results.meta.fields ||
          (results.data[0] ? Object.keys(results.data[0] as CSVRow) : []);

        if (!headers || headers.length === 0) {
          reject(new Error("CSV file must include a header row."));
          return;
        }

        if (!results.data || results.data.length === 0) {
          reject(new Error("CSV file contains no data rows."));
          return;
        }

        let headerToFieldKey: Record<string, string>;
        try {
          headerToFieldKey = buildAnswerItemsHeaderMap(element, headers);
        } catch (error) {
          reject(error);
          return;
        }

        const fieldKeyToHeader = Object.entries(headerToFieldKey).reduce(
          (acc, [header, fieldKey]) => {
            acc[fieldKey] = header;
            return acc;
          },
          {} as Record<string, string>
        );

        const startingSequential =
          getHighestSequentialTypeNumber(existingItems);
        const importedItems: AnswerItem[] = [];

        (results.data as CSVRow[]).forEach((row) => {
          const isEmptyRow = Object.keys(headerToFieldKey).every((header) => {
            const value = row[header];
            return value === undefined || value === "";
          });

          if (isEmptyRow) {
            return;
          }

          const fields: Record<string, string> = {};
          element.options.fields.forEach((field) => {
            const header = fieldKeyToHeader[field.fieldKey];
            const rawValue = header ? row[header] : "";
            let value = rawValue ?? "";
            if (typeof value === "string") {
              value = value.replace(/\r?\n/g, "<br/>");
            } else if (value !== "") {
              value = String(value);
            }
            fields[field.fieldKey] = value as string;
          });

          const sequentialTypeNumber =
            startingSequential + importedItems.length + 1;

          importedItems.push({
            type: element.options.typeId,
            sequentialTypeNumber,
            id: crypto.randomUUID(),
            fields,
            updatedAt: new Date(),
            createdAt: new Date(),
            createdBy: "",
            organizationId: "",
            archived: false,
          });
        });

        resolve(importedItems);
      },
      error: (error) => {
        reject(new Error(`Error parsing CSV: ${error.message}`));
      },
    });
  });
